import React from 'react';
import * as helpers from '../utils/analysisHelpers';
import { useAnalysisData } from '../hooks/useAnalysisData'; 

import SalesSection from '../components/analysis/SalesSection';
import PartsSection from '../components/analysis/PartsSection';
import AsSection from '../components/analysis/AsSection';
import CustomerSection from '../components/analysis/CustomerSection';
import VehicleSection from '../components/analysis/VehicleSection';
import AiSection from '../components/analysis/AiSection';

const Analysis = ({ user }) => {
  const {
    loadData, subTab, setSubTab,
    salesPeriod, setSalesPeriod, targetDate, setTargetDate, salesSearch, setSalesSearch,
    usedPartsPeriod, setUsedPartsPeriod, usedPartsDate, setUsedPartsDate, selectedModel, setSelectedModel,
    usedPartsModelFilter, setUsedPartsModelFilter, usedPartsSearch, setUsedPartsSearch,
    asFilterPeriod, setAsFilterPeriod, asFilterDate, setAsFilterDate,
    custModelFilter, setCustModelFilter, custTypeFilter, setCustTypeFilter,
    stats, vehicles, asRecords, customers, partsValue,
    periodDetailList, periodTotalSales, monthlySalesTrend,
    periodTotalUsedQty, periodTotalUsedCost, aggregatedPartsRanking,
    filteredParts, filteredTotalValue, filteredCustomersList, masterModels,
    displayDate, todayStr
  } = useAnalysisData();
  const canSeeAi = !!user && (user.role === 'ADMIN' || user.role === 'STAFF');

  const onPrintSales = () => helpers.handlePrintSales(salesPeriod, targetDate, periodDetailList, periodTotalSales);
  const onDownloadSalesExcel = () => helpers.handleDownloadSalesExcel(periodDetailList, targetDate);
  const onPrintUsedParts = () => helpers.handlePrintUsedParts(displayDate, usedPartsModelFilter, usedPartsSearch, periodTotalUsedQty, periodTotalUsedCost, aggregatedPartsRanking);
  const onDownloadUsedPartsExcel = () => helpers.handleDownloadUsedPartsExcel(aggregatedPartsRanking, displayDate);
  const onPrintStockParts = () => helpers.handlePrintStockParts(selectedModel, filteredParts, filteredTotalValue);
  const onDownloadStockPartsExcel = () => helpers.handleDownloadStockPartsExcel(filteredParts);
  const onPrintWarrantyReport = (type) => helpers.handlePrintWarrantyReport(type, vehicles, todayStr);
  
  // 🚀 [추가 주입] 'monthlySalesTrend'를 가장 뒤에 파라미터로 넘겨주어 출력물에 그래프를 그려냅니다!
  const onPrintGlobalReport = () => helpers.handlePrintReport(stats, asRecords, customers, periodTotalSales, salesPeriod, partsValue, usedPartsPeriod, periodTotalUsedQty, periodTotalUsedCost, filteredTotalValue, selectedModel, todayStr, monthlySalesTrend);

  const style = {
    wrapper: { padding: '40px 100px', background: '#f8fafc', minHeight: '100vh' },
    tabMenuBar: { display: 'flex', gap: '8px', background: '#f1f5f9', padding: '6px', borderRadius: '12px', border: '1px solid #e2e8f0', marginLeft: '30px' },
    tabButton: (isActive) => ({
      padding: '10px 22px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
      background: isActive ? '#1e293b' : 'transparent', color: isActive ? 'white' : '#64748b',
      boxShadow: isActive ? '0 4px 12px rgba(30,41,59,0.15)' : 'none', transition: 'all 0.2s'
    })
  };

  return (
    <div style={style.wrapper}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px'}}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <h2 style={{margin: 0, fontSize: '26px', color: '#1e293b'}}>📊 종합 분석</h2>
          <div style={style.tabMenuBar}>
            <button onClick={() => setSubTab('sales')} style={style.tabButton(subTab === 'sales')}>매출분석</button>
            <button onClick={() => setSubTab('parts')} style={style.tabButton(subTab === 'parts')}>부품분석</button>
            <button onClick={() => setSubTab('as')} style={style.tabButton(subTab === 'as')}>AS분석</button>
            <button onClick={() => setSubTab('customer')} style={style.tabButton(subTab === 'customer')}>고객분석</button>
            <button onClick={() => setSubTab('vehicle')} style={style.tabButton(subTab === 'vehicle')}>차량분석</button>
            {/* AI 분석 보고서에는 보증 만료 고객 연락처가 들어가므로 외부 열람 계정(VIEWER)에는 보이지 않는다(규칙도 막음). */}
            {canSeeAi && <button onClick={() => setSubTab('ai')} style={style.tabButton(subTab === 'ai')}>🤖 AI 분석</button>}
          </div>
        </div>
        
        <div style={{display: 'flex', gap: '12px'}}>
          <button onClick={onPrintGlobalReport} style={{padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer'}}>
            🖨️ 리포트 PDF 출력
          </button>
          <button onClick={loadData} style={{padding: '10px 20px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer'}}>
            🔄 지표 실시간 동기화
          </button>
        </div>
      </div>

      {subTab === 'sales' && (
        <SalesSection 
          stats={stats} salesPeriod={salesPeriod} setSalesPeriod={setSalesPeriod}
          targetDate={targetDate} setTargetDate={setTargetDate}
          salesSearch={salesSearch} setSalesSearch={setSalesSearch} 
          monthlySalesTrend={monthlySalesTrend} 
          periodDetailList={periodDetailList} periodTotalSales={periodTotalSales}
          handlePrintSales={onPrintSales} handleDownloadSalesExcel={onDownloadSalesExcel}
        />
      )}

      {subTab === 'parts' && (
        <PartsSection 
          partsValue={partsValue} periodTotalUsedQty={periodTotalUsedQty} periodTotalUsedCost={periodTotalUsedCost}
          usedPartsPeriod={usedPartsPeriod} setUsedPartsPeriod={setUsedPartsPeriod}
          usedPartsDate={usedPartsDate} setUsedPartsDate={setUsedPartsDate}
          aggregatedPartsRanking={aggregatedPartsRanking}
          usedPartsModelFilter={usedPartsModelFilter} setUsedPartsModelFilter={setUsedPartsModelFilter}
          usedPartsSearch={usedPartsSearch} setUsedPartsSearch={setUsedPartsSearch}
          selectedModel={selectedModel} setSelectedModel={setSelectedModel}
          filteredParts={filteredParts} filteredTotalValue={filteredTotalValue}
          handlePrintUsedParts={onPrintUsedParts} handleDownloadUsedPartsExcel={onDownloadUsedPartsExcel}
          handlePrintStockParts={onPrintStockParts} handleDownloadStockPartsExcel={onDownloadStockPartsExcel}
        />
      )}

      {subTab === 'as' && (
        <AsSection 
          stats={stats} asFilterPeriod={asFilterPeriod} setAsFilterPeriod={setAsFilterPeriod}
          asFilterDate={asFilterDate} setAsFilterDate={setAsFilterDate}
        />
      )}

      {subTab === 'customer' && (
        <CustomerSection 
          filteredCustomersList={filteredCustomersList} customers={customers}
          custModelFilter={custModelFilter} setCustModelFilter={setCustModelFilter}
          custTypeFilter={custTypeFilter} setCustTypeFilter={setCustTypeFilter}
          masterModels={masterModels}
        />
      )}

      {subTab === 'vehicle' && (
        <VehicleSection stats={stats} handlePrintWarrantyReport={onPrintWarrantyReport} />
      )}

      {subTab === 'ai' && canSeeAi && <AiSection user={user} />}
    </div>
  );
};

export default Analysis;